import React from 'react';
import { Outlet } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { normalizeAccountType } from '../admin/shared/accountTypeUtils';

/**
 * AccountTypeProtectedRoute
 *
 * Blocks a set of account-portal routes for specific account types.
 *
 * Currently used to hide the account-level Points Engine pages
 * (Earning Rules, Redemption Rules, Rewards Options, Terms & Conditions)
 * and Brand Management from the MULTI_VENDOR account portal.
 *
 * In a Multi-Vendor marketplace each shop runs its own loyalty program, so the
 * mall operator (account admin) must not configure account-level loyalty rules.
 * These pages remain fully available to Unified Brand / Essential / Single Seller
 * accounts. See backend/docs/TWO_MODEL_ARCHITECTURE.md §5.3.
 *
 * BOTH sides of the comparison are normalised (`trim` + `toLowerCase` + strip
 * separators) so a legacy spelling such as `Multi-Vendor` or `multi_vendor`
 * cannot slip past an exact-match list and fail OPEN. Unknown / missing account
 * types are not blocked - the route renders and the page decides.
 *
 * @param {string[]} blockedAccountTypes - account_type values to block, in any
 *   accepted spelling (normalised before comparison).
 * @param {string} redirectTo - path to redirect to when blocked.
 */
const AccountTypeProtectedRoute = ({
  blockedAccountTypes = ['multivendor'],
  redirectTo = '/admin/dashboard',
}) => {
  const { account: accountData } = useSelector((state) => state.loyalty.admin);
  const accountType = normalizeAccountType(accountData?.account_type);
  const blockedTypes = (blockedAccountTypes || []).map(normalizeAccountType);

  if (accountType && blockedTypes.includes(accountType)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
};

export default AccountTypeProtectedRoute;
